import { UserPermission } from './user';

export enum Location {
  EMPTY = '/',
  ONBOARDING_SETUP = '/onboarding',
  HOME = '/home',
  MONITORING = '/monitoring',
  TRACKING_HISTORY = '/tracking-history',
  TRACKING = '/tracking',
  ROUTING = '/routing',
  SERVICES_AND_DELIVERIES_PLANNING = '/services-and-deliveries-planning',
  ROUTE_EDIT = '/route-edit',
  ACTIVITY_EDIT = '/activity-edit',
  SERVICES_AND_DELIVERIES_CONTROL_TOWER = '/services-and-deliveries-control-tower',
  SERVICE_TRACKING = '/service-tracking',
  ALERTS = '/alerts',
  DRIVER_BEHAVIOR = '/driver-behavior',
  SPEEDING = '/speeding',
  PRODUCTIVITY = '/productivity',
  REPORTS = '/reports',

  // fleet policy
  FLEET_POLICY = '/fleet-policy',
  FLEET_POLICY_TYPE = '/fleet-policy/trigger-events/:type',
  FLEET_POLICY_GEOFENCE = '/fleet-policy/trigger-events/geofence',
  FLEET_POLICY_IDLE_ENGINE = '/fleet-policy/trigger-events/idle-engine',
  FLEET_POLICY_DEVICE_CONNECTION = '/fleet-policy/trigger-events/device-connection',
  FLEET_POLICY_VEHICLE_SPEED_LIMIT = '/fleet-policy/trigger-events/vehicle-speed-limit',
  FLEET_POLICY_ROAD_SPEED_LIMIT = '/fleet-policy/trigger-events/road-speed-limit',
  FLEET_POLICY_LP = '/fleet-policy/landing',
  TRIGGERS = '/fleet-policy/triggers',
  TRIGGERS_CREATE = '/fleet-policy/triggers/create',
  TRIGGERS_EDIT = '/fleet-policy/triggers/edit',

  // new reports
  REPORTS_NEW = '/report',
  REPORTS_TRIP_AND_STOP = '/report/trip-and-stop',
  REPORTS_TRIP_AND_STOP_TYPE = '/report/trip-and-stop/:type',
  REPORTS_TRIP_AND_STOP_DAILY = '/report/trip-and-stop/:type/daily',
  REPORTS_TRIP_AND_STOP_DETAILS = '/report/trip-and-stop/details',
  REPORTS_FLEET_SAFETY_TYPE = '/report/safety/:type',

  ADVANCED_ANALYTICS = '/advanced-analytics',
  INSURANCE = '/insurance',
  PROOF_OF_CONCLUSION = '/proof-of-conclusion',
  FUEL_CARD = '/fuel-card',
  MAINTENANCE = '/maintenance',
  COSTS = '/costs',
  VEHICLES = '/vehicles',
  VEHICLES_ID = '/vehicles/:vehicleId',
  DEVICES = '/devices',
  VEHICLES_GROUPS = '/vehicle-groups',
  VEHICLES_GROUPS_ID = '/vehicle-groups/:vehicleId',
  DEVICES_ID = '/devices/:deviceId',
  DRIVER_MANAGEMENT = '/driver-management',
  DRIVER_MANAGEMENT_ID = '/driver-management/:driverId',
  CAMERA = '/camera',
  CAMERA_EVENT = '/camera-event/:type',
  CAMERA_EVENT_FAVORITE = '/camera-event/favorite',
  CAMERA_EVENT_ALL_EVENTS = '/camera-event/all-events',
  CAMERA_REQUEST = '/camera-request/:type',
  CAMERA_REQUEST_FAVORITE = '/camera-request/favorite',
  CAMERA_REQUEST_ALL_REQUESTS = '/camera-request/all-requests',
  CAMERA_TRIP_IMAGE_HISTORY = '/trip-image-history/:type',
  CAMERA_TRIP_IMAGE_HISTORY_ALL_TRIPS = '/trip-image-history/all-trips',
  CAMERA_TRIP_IMAGE_HISTORY_PENDING_TRIPS = '/trip-image-history/pending-nominate',
  MULTICAM_LANDING_PAGE = '/multicam',
  CHECKLIST = '/checklist',
  APPS = '/apps',
  APIS = '/apis',
  WEBHOOKS = '/webhooks',
  CONFIG = '/config',
  PASSWORD_RECOVERY = '/password-recovery',
  TOKEN_VALIDATION = '/already-have-token',
  PASSWORD_RESET = '/password-reset',
  LOGIN = '/login',
  LOGOUT = '/logout',
  NOT_FOUND = '/not-found',
  NO_FLEET_ACCESS = '/no-fleet-access',
  CRM_DEVOLUTION = '/crm/devolucao',
}

// Every location must have a mapped name for page view
export const locationName: Record<Location, string> = {
  [Location.EMPTY]: '',
  [Location.HOME]: 'Início',
  [Location.ONBOARDING_SETUP]: 'Configuração inicial',
  [Location.MONITORING]: 'Monitoramento',
  [Location.TRACKING_HISTORY]: 'Trajetos',
  [Location.TRACKING]: 'Trajeto',
  [Location.ROUTING]: 'Gerar rotas',
  [Location.SERVICES_AND_DELIVERIES_PLANNING]: 'Adicionar',
  [Location.ROUTE_EDIT]: 'Edição de rota',
  [Location.ACTIVITY_EDIT]: 'Edição de atividade',
  [Location.SERVICES_AND_DELIVERIES_CONTROL_TOWER]: 'Torre de controle',
  [Location.SERVICE_TRACKING]: 'Acompanhar rotas',
  [Location.DRIVER_BEHAVIOR]: 'Modo de condução',
  [Location.PRODUCTIVITY]: 'Produtividade',
  [Location.SPEEDING]: 'Velocidade',
  [Location.REPORTS]: 'Relatórios',
  [Location.REPORTS_NEW]: 'Relatórios 2',
  [Location.REPORTS_TRIP_AND_STOP]: 'Relatório de trajetos e paradas',
  [Location.REPORTS_TRIP_AND_STOP_TYPE]: 'Relatório de trajetos e paradas',
  [Location.REPORTS_TRIP_AND_STOP_DETAILS]:
    'Relatório de trajetos e paradas detalhado',
  [Location.REPORTS_TRIP_AND_STOP_DAILY]:
    'Relatório de trajetos e paradas diário',
  [Location.REPORTS_FLEET_SAFETY_TYPE]: 'Relatório de segurança da frota',
  [Location.ADVANCED_ANALYTICS]: 'Análises Avançadas',
  [Location.INSURANCE]: 'Seguros',
  [Location.PROOF_OF_CONCLUSION]: 'Comprovantes',
  [Location.FUEL_CARD]: 'Gestão de combustível',
  [Location.MAINTENANCE]: 'Manutenção',
  [Location.COSTS]: 'Gastos gerais',
  [Location.VEHICLES]: 'Veículos',
  [Location.VEHICLES_ID]: 'Veículos',
  [Location.VEHICLES_GROUPS]: 'Grupos de Veículos',
  [Location.VEHICLES_GROUPS_ID]: 'Grupo de Veículos',
  [Location.DEVICES]: 'Dispositivos',
  [Location.DEVICES_ID]: 'Dispositivo',
  [Location.DRIVER_MANAGEMENT]: 'Motoristas',
  [Location.DRIVER_MANAGEMENT_ID]: 'Motorista',
  [Location.CHECKLIST]: 'Checklists',
  [Location.APPS]: 'Aplicativos',
  [Location.APIS]: 'APIs',
  [Location.WEBHOOKS]: 'Webhooks',
  [Location.ALERTS]: 'Alertas',
  [Location.FLEET_POLICY]: 'Política de frota',
  [Location.FLEET_POLICY_TYPE]: 'Política de frota',
  [Location.FLEET_POLICY_GEOFENCE]: 'Política de frota - Gestão de locais',
  [Location.FLEET_POLICY_IDLE_ENGINE]: 'Política de frota - Motor ocioso',
  [Location.FLEET_POLICY_DEVICE_CONNECTION]:
    'Política de frota - Situação do dispositivo',
  [Location.FLEET_POLICY_VEHICLE_SPEED_LIMIT]:
    'Política de frota - Velocidade do veículo',
  [Location.FLEET_POLICY_ROAD_SPEED_LIMIT]:
    'Política de frota - Velocidade da via',
  [Location.FLEET_POLICY_LP]: 'Política de frota',
  [Location.TRIGGERS]: 'Política de frota',
  [Location.TRIGGERS_CREATE]: 'Política de frota',
  [Location.TRIGGERS_EDIT]: 'Política de frota',
  [Location.CAMERA]: 'Cobli Cam',
  [Location.CONFIG]: 'Configurações gerais',
  [Location.PASSWORD_RECOVERY]: 'Esqueceu a senha',
  [Location.TOKEN_VALIDATION]: 'Validação de código de segurança',
  [Location.PASSWORD_RESET]: 'Definir nova senha',
  [Location.LOGIN]: 'Login',
  [Location.LOGOUT]: 'Sair do sistema',
  [Location.CAMERA_EVENT]: 'Eventos de vídeo',
  [Location.CAMERA_EVENT_FAVORITE]: 'Eventos de vídeo',
  [Location.CAMERA_EVENT_ALL_EVENTS]: 'Eventos de vídeo',
  [Location.CAMERA_TRIP_IMAGE_HISTORY]: 'Identificação facial',
  [Location.CAMERA_TRIP_IMAGE_HISTORY_ALL_TRIPS]: 'Identificação facial',
  [Location.CAMERA_TRIP_IMAGE_HISTORY_PENDING_TRIPS]: 'Identificação facial',
  [Location.MULTICAM_LANDING_PAGE]: 'Cobli Cam',
  [Location.CAMERA_REQUEST]: 'Vídeos solicitados',
  [Location.CAMERA_REQUEST_ALL_REQUESTS]: 'Vídeos solicitados',
  [Location.CAMERA_REQUEST_FAVORITE]: 'Vídeos solicitados',
  [Location.NOT_FOUND]: 'Página não encontrada',
  [Location.NO_FLEET_ACCESS]: 'Frota sem acesso',
  [Location.CRM_DEVOLUTION]: 'Devolução de dispositivos',
};

// Every location must have a minimum user permission mapped. Or none if no special permission is required
export const locationUserPermission: Record<Location, UserPermission> = {
  [Location.EMPTY]: UserPermission.BASIC_PERMISSION,
  [Location.HOME]: UserPermission.BASIC_PERMISSION,
  [Location.ONBOARDING_SETUP]: UserPermission.BASIC_PERMISSION,
  [Location.MONITORING]: UserPermission.BASIC_PERMISSION,
  [Location.TRACKING_HISTORY]: UserPermission.BASIC_PERMISSION,
  [Location.TRACKING]: UserPermission.BASIC_PERMISSION,
  [Location.ROUTING]: UserPermission.CREATE_UPDATE_DELETE_ROUTES,
  [Location.SERVICES_AND_DELIVERIES_PLANNING]:
    UserPermission.CREATE_UPDATE_DELETE_ROUTES,
  [Location.ROUTE_EDIT]: UserPermission.CREATE_UPDATE_DELETE_ROUTES,
  [Location.ACTIVITY_EDIT]: UserPermission.CREATE_UPDATE_DELETE_ROUTES,
  [Location.SERVICES_AND_DELIVERIES_CONTROL_TOWER]: UserPermission.VIEW_ROUTES,
  [Location.SERVICE_TRACKING]: UserPermission.VIEW_ROUTES,
  [Location.DRIVER_BEHAVIOR]: UserPermission.VIEW_FLEET_SAFETY,
  [Location.DRIVER_MANAGEMENT_ID]: UserPermission.VIEW_FLEET_SAFETY,
  [Location.PRODUCTIVITY]: UserPermission.VIEW_ANALYTICS_AND_REPORTS,
  [Location.SPEEDING]: UserPermission.VIEW_FLEET_SAFETY,
  [Location.REPORTS]: UserPermission.VIEW_ANALYTICS_AND_REPORTS,
  [Location.REPORTS_NEW]: UserPermission.VIEW_ANALYTICS_AND_REPORTS,
  [Location.REPORTS_TRIP_AND_STOP]: UserPermission.VIEW_ANALYTICS_AND_REPORTS,
  [Location.REPORTS_TRIP_AND_STOP_DETAILS]:
    UserPermission.VIEW_ANALYTICS_AND_REPORTS,

  // use only for permission
  [Location.REPORTS_TRIP_AND_STOP_TYPE]:
    UserPermission.VIEW_ANALYTICS_AND_REPORTS,
  [Location.REPORTS_TRIP_AND_STOP_DAILY]:
    UserPermission.VIEW_ANALYTICS_AND_REPORTS,
  [Location.REPORTS_FLEET_SAFETY_TYPE]:
    UserPermission.VIEW_ANALYTICS_AND_REPORTS,
  [Location.ADVANCED_ANALYTICS]: UserPermission.VIEW_ANALYTICS_AND_REPORTS,
  [Location.INSURANCE]: UserPermission.VIEW_FLEET_SAFETY,
  [Location.PROOF_OF_CONCLUSION]: UserPermission.VIEW_PROOF_OF_CONCLUSION,
  [Location.FUEL_CARD]: UserPermission.VIEW_FUEL_CARD,
  [Location.MAINTENANCE]: UserPermission.FULL_CONTROL_MAINTENANCE,
  [Location.COSTS]: UserPermission.VIEW_FLEET_EXPENSES,
  [Location.VEHICLES]: UserPermission.BASIC_PERMISSION,
  [Location.VEHICLES_GROUPS]: UserPermission.BASIC_PERMISSION,
  [Location.VEHICLES_GROUPS_ID]: UserPermission.BASIC_PERMISSION,
  [Location.VEHICLES_ID]: UserPermission.BASIC_PERMISSION,
  [Location.DEVICES]: UserPermission.BASIC_PERMISSION,
  [Location.DEVICES_ID]: UserPermission.BASIC_PERMISSION,
  [Location.DRIVER_MANAGEMENT]: UserPermission.BASIC_PERMISSION,
  [Location.CHECKLIST]: UserPermission.FULL_CONTROL_CHECKLIST,
  [Location.APPS]: UserPermission.ADMINISTRATE_API_INTEGRATIONS,
  [Location.APIS]: UserPermission.ADMINISTRATE_API_INTEGRATIONS,
  [Location.WEBHOOKS]: UserPermission.ADMINISTRATE_API_INTEGRATIONS,
  [Location.ALERTS]: UserPermission.CONFIG_AND_VIEW_CUSTOM_ALERTS,
  [Location.FLEET_POLICY]: UserPermission.CONFIG_AND_VIEW_CUSTOM_ALERTS,
  [Location.FLEET_POLICY_TYPE]: UserPermission.CONFIG_AND_VIEW_CUSTOM_ALERTS,

  // use only for permission
  [Location.FLEET_POLICY_GEOFENCE]:
    UserPermission.CONFIG_AND_VIEW_CUSTOM_ALERTS,
  [Location.FLEET_POLICY_IDLE_ENGINE]:
    UserPermission.CONFIG_AND_VIEW_CUSTOM_ALERTS,
  [Location.FLEET_POLICY_DEVICE_CONNECTION]:
    UserPermission.CONFIG_AND_VIEW_CUSTOM_ALERTS,
  [Location.FLEET_POLICY_VEHICLE_SPEED_LIMIT]:
    UserPermission.CONFIG_AND_VIEW_CUSTOM_ALERTS,
  [Location.FLEET_POLICY_ROAD_SPEED_LIMIT]:
    UserPermission.CONFIG_AND_VIEW_CUSTOM_ALERTS,

  [Location.FLEET_POLICY_LP]: UserPermission.CONFIG_AND_VIEW_CUSTOM_ALERTS,
  [Location.TRIGGERS]: UserPermission.CONFIG_AND_VIEW_CUSTOM_ALERTS,
  [Location.TRIGGERS_CREATE]: UserPermission.CONFIG_AND_VIEW_CUSTOM_ALERTS,
  [Location.TRIGGERS_EDIT]: UserPermission.CONFIG_AND_VIEW_CUSTOM_ALERTS,
  [Location.CAMERA]: UserPermission.BASIC_PERMISSION,
  [Location.CONFIG]: UserPermission.BASIC_PERMISSION,
  [Location.PASSWORD_RECOVERY]: UserPermission.BASIC_PERMISSION,
  [Location.TOKEN_VALIDATION]: UserPermission.BASIC_PERMISSION,
  [Location.PASSWORD_RESET]: UserPermission.BASIC_PERMISSION,
  [Location.LOGIN]: UserPermission.BASIC_PERMISSION,
  [Location.LOGOUT]: UserPermission.BASIC_PERMISSION,
  [Location.CAMERA_EVENT]: UserPermission.VIEW_CAMERA_MEDIA,
  [Location.CAMERA_EVENT_FAVORITE]: UserPermission.VIEW_CAMERA_MEDIA,
  [Location.CAMERA_EVENT_ALL_EVENTS]: UserPermission.VIEW_CAMERA_MEDIA,
  [Location.MULTICAM_LANDING_PAGE]: UserPermission.BASIC_PERMISSION,
  [Location.CAMERA_REQUEST]: UserPermission.VIEW_CAMERA_MEDIA,
  [Location.CAMERA_REQUEST_ALL_REQUESTS]: UserPermission.VIEW_CAMERA_MEDIA,
  [Location.CAMERA_REQUEST_FAVORITE]: UserPermission.VIEW_CAMERA_MEDIA,
  [Location.CAMERA_TRIP_IMAGE_HISTORY]: UserPermission.VIEW_CAMERA_MEDIA,
  [Location.CAMERA_TRIP_IMAGE_HISTORY_ALL_TRIPS]:
    UserPermission.VIEW_CAMERA_MEDIA,
  [Location.CAMERA_TRIP_IMAGE_HISTORY_PENDING_TRIPS]:
    UserPermission.VIEW_CAMERA_MEDIA,
  [Location.NOT_FOUND]: UserPermission.BASIC_PERMISSION,
  [Location.NO_FLEET_ACCESS]: UserPermission.BASIC_PERMISSION,
  [Location.CRM_DEVOLUTION]: UserPermission.BASIC_PERMISSION,
};
